// nav-link.js — the app's nav state <-> a URL you can paste.
//
// Alex, 2026-08-05: "it has to be very basic, simply to make it easy for me to let you
// know what you have to check, or the same thing if you want me to check something...
// And later it would be cool that I can use that link to send email to somebody else."
//
// WHY THERE WAS NO LINK. App already serialises its whole nav into `navSig` for the
// History API — but calls `history.pushState(state, '')` with NO url, so the address bar
// never moves off `/?view=products`. The state was always there; it just never reached
// the URL. So this is not a new navigation model, it is the existing one made visible.
//
// OPAQUE ROUND-TRIP, ON PURPOSE. catalogNodeId is 'c3-287' or 'c3-287|fam-35',
// productNodeId has its own shape, and both may change. Nothing here parses them — they
// go out as strings and come back as strings. A serialiser that understood their format
// would be a second place to update every time one changes.
//
// `node` stays the CATALOG parameter: `?view=catalog&node=c3-287|fam-35` links already
// exist and are handled in App, and breaking a link that someone has already pasted
// somewhere is exactly the failure this feature is meant to remove.

export const VIEW_PARAM = 'view';

// nav: { view, catalogNodeId, productNodeId, projectsTarget:{projectId,functionId,placementId} }
// Returns a query string beginning with '?' — or '' when there is nothing worth linking.
export function navToQuery(nav) {
  if (!nav?.view) return '';
  const q = new URLSearchParams();
  q.set(VIEW_PARAM, nav.view);
  if (nav.catalogNodeId) q.set('node', String(nav.catalogNodeId));
  if (nav.productNodeId) q.set('prod', String(nav.productNodeId));
  const t = nav.projectsTarget;
  if (t?.projectId != null) q.set('proj', String(t.projectId));
  if (t?.functionId != null) q.set('func', String(t.functionId));
  if (t?.placementId != null) q.set('place', String(t.placementId));
  return `?${q.toString()}`;
}

// Parse a query string back. Returns only the keys actually present, so a caller can
// distinguish "not in the link" from "explicitly empty" and leave its own state alone.
export function navFromQuery(search, validViews = null) {
  const out = {};
  let q;
  try { q = new URLSearchParams(search || ''); } catch { return out; }

  const v = q.get(VIEW_PARAM);
  if (v && (!validViews || validViews.includes(v))) out.view = v;
  if (q.get('node')) out.catalogNodeId = q.get('node');
  if (q.get('prod')) out.productNodeId = q.get('prod');

  // A projects target needs at least a project; a bare func/place has nothing to open
  // against and is dropped rather than half-applied.
  const proj = q.get('proj');
  if (proj) {
    out.projectsTarget = {
      projectId: proj,
      functionId: q.get('func') || undefined,
      placementId: q.get('place') || undefined,
    };
  }
  return out;
}

// The absolute link to copy. Absolute from the CURRENT origin, deliberately: today that
// is http://localhost:8002 and only useful to Alex and this session, but the day the app
// has a real host the same links become emailable with no change here — whereas a
// relative link would have had to be rewritten everywhere it had been pasted.
export function absoluteNavLink(nav, origin, pathname = '/') {
  return `${origin}${pathname}${navToQuery(nav)}`;
}

// dbHrefFor(view, nodeId) - the deep link to ONE item, given the tree coordinate the
// server already hands back (`/api/onshape/by-ref` returns `{ view, nodeId }`, and the
// Onshape screen's tree carries `_srcId`).
//
// THIS EXISTS BECAUSE BOTH CALLERS GOT IT WRONG THE SAME WAY, and it failed silently.
// They built the link by hand as `/?view=${view}&node=${nodeId}` - correct for the
// catalog, WRONG for a product: `node` parses back as `catalogNodeId` (above), and the
// Products view reads `productNodeId` only. So `?view=products&node=c3-343|900` opened
// Products at whatever was last selected, with no error and no empty state - the link
// looked like it worked. Same failure mode as the line-vs-family node id in
// scripts/link.mjs: a well-formed string that names nothing.
//
// The repo's rule is that a link is a place he can go (CLAUDE.md, 2026-08-19). A link
// that lands somewhere else is worse than the bare id it replaced, because the bare id
// at least announces that a lookup is needed.
export function dbHrefFor(view, nodeId) {
  if (!view || !nodeId) return '/';
  const nav = view === 'products'
    ? { view, productNodeId: nodeId }
    : { view, catalogNodeId: nodeId };
  return `/${navToQuery(nav)}`;
}
